import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import * as api from '../../utils/api';

const initialState = () => ({ drafts: [], selectedDraftId: null, pending: null, error: null, listReady: false });
const sameId = (a, b) => Number(a) === Number(b);
const sorted = drafts => [...drafts].sort((a, b) =>
    String(a.dateCreated).localeCompare(String(b.dateCreated)) || Number(a.draftId) - Number(b.draftId));
const storeSnapshot = (prev, draft, select = true) => ({ ...prev,
    drafts: sorted([...prev.drafts.filter(row => !sameId(row.draftId, draft.draftId)), draft]),
    selectedDraftId: select ? Number(draft.draftId) : prev.selectedDraftId,
});

function safeError(error) {
    const status = error.response?.status;
    const message = error.response?.data?.error;
    if ([400, 404, 409].includes(status) && typeof message === 'string' && message.trim()) return message;
    if ([401, 403].includes(status)) return 'You do not have access to these character drafts.';
    return 'Unable to complete the draft request. Please try again.';
}

function requireValidDetail(detail) {
    if (detail?.validation?.isValid !== true || !Array.isArray(detail.validation.errors) || detail.validation.errors.length) {
        // Do not treat a list entry, missing validation or a stale-invalid HTTP
        // 200 response as a loadable build. Phase 5 may show the structured errors.
        const error = new Error('This draft is not currently valid for this character.');
        error.code = 'DRAFT_VALIDATION';
        error.draftValidation = detail?.validation;
        throw error;
    }
    return detail;
}

// Editor-local snapshot state only. No currentCharacter copy and no canonical
// saves. Phase 5 composes these operations with comparison/prompt/application.
// Every async callback resolves { status: 'success', data }, { status: 'error',
// error }, or { status: 'stale' }; callers must check status before proceeding.
// A completed write overtaken within this scope returns { status: 'superseded',
// data } and still reconciles the list, without taking over the newer selection.
// Superseded write failures return status 'error' with superseded: true without
// replacing the newer UI error. 'stale' means the caller must stop, not that a
// server write was cancelled (scope changes/unmount cannot cancel accepted writes).
export default function useCharacterDrafts({ characterId, token }) {
    const id = characterId == null ? null : Number(characterId);
    const scope = useMemo(() => ({ id, token }), [id, token]);
    const currentScope = useRef(scope);
    currentScope.current = scope;
    const mounted = useRef(false);
    const generation = useRef(0);
    const lifetime = useRef(0);
    const mutations = useRef(new Map());
    const mutationRevision = useRef(0);
    const [state, setState] = useState(initialState);

    useLayoutEffect(() => {
        mounted.current = true;
        lifetime.current += 1;
        mutations.current = new Map();
        generation.current += 1;
        setState(initialState());
        return () => { mounted.current = false; generation.current += 1; };
    }, [scope]);

    const run = useCallback(async (operation, request, accept, mutationKey, prepare = data => data) => {
        if (!mounted.current || currentScope.current !== scope || !id) return { status: 'stale' };
        const requestGeneration = ++generation.current;
        const requestLifetime = lifetime.current;
        const inScope = () => mounted.current && currentScope.current === scope && lifetime.current === requestLifetime;
        const active = () => inScope() && generation.current === requestGeneration;
        const isMutation = mutationKey !== undefined;
        setState(prev => ({ ...prev, pending: operation, error: null }));
        try {
            const response = await request();
            if (!inScope() || (!isMutation && !active())) return { status: 'stale' };
            const data = prepare(response);
            if (isMutation) {
                mutations.current.set(Number(mutationKey(data)), {
                    revision: ++mutationRevision.current,
                    draft: operation === 'delete' ? null : data,
                });
            }
            // Decide freshness when the response arrives. React may batch this
            // update with a following refresh/selection; those queued updates
            // already run in order and must not retroactively cancel this result.
            const acceptedCurrent = active();
            setState(prev => {
                if (!inScope()) return prev;
                const next = accept(prev, data, acceptedCurrent);
                return acceptedCurrent ? { ...next, pending: null, error: null } : next;
            });
            return { status: acceptedCurrent ? 'success' : 'superseded', data };
        } catch (cause) {
            if (!inScope() || (!isMutation && !active())) return { status: 'stale' };
            const error = cause.code === 'DRAFT_VALIDATION'
                ? cause.message : safeError(cause);
            const acceptedCurrent = active();
            setState(prev => inScope() && acceptedCurrent ? { ...prev, pending: null, error } : prev);
            return { status: 'error', error, validation: cause.draftValidation,
                ...(!active() ? { superseded: true } : {}) };
        }
    }, [scope, id]);

    const refresh = useCallback(() => {
        const startedAt = mutationRevision.current;
        // A list fetched before a write completed must not resurrect a deletion
        // or undo an acknowledged save/rename. Include those completions in both
        // the returned comparison list and the stored list.
        const reconcile = rows => {
            if (!Array.isArray(rows)) throw new Error('Invalid draft list.');
            const merged = new Map(rows.map(row => [Number(row.draftId), row]));
            mutations.current.forEach(({ revision, draft }, draftId) => {
                if (revision <= startedAt) return;
                if (draft) merged.set(draftId, draft);
                else merged.delete(draftId);
            });
            return sorted([...merged.values()]);
        };
        return run('refresh', () => api.fetchCharacterDrafts(id), (prev, rows) => {
            const drafts = reconcile(rows);
            return { ...prev, drafts, listReady: true,
                selectedDraftId: drafts.some(draft => sameId(draft.draftId, prev.selectedDraftId)) ? prev.selectedDraftId : null };
        }, undefined, reconcile);
    }, [id, run]);

    useEffect(() => { refresh(); }, [refresh]);

    const selectDraft = useCallback(draftId => {
        if (!mounted.current || currentScope.current !== scope) return;
        generation.current += 1;
        setState(prev => ({ ...prev, selectedDraftId: draftId == null ? null : Number(draftId), pending: null, error: null }));
    }, [scope]);

    const fetchDraft = useCallback(draftId => run('detail', async () => {
        const detail = await api.fetchCharacterDraft(id, draftId);
        if (!sameId(detail?.draftId, draftId) || !sameId(detail?.characterId, id)) throw new Error('Invalid draft response.');
        return requireValidDetail(detail);
    }, prev => prev), [id, run]);

    const createDraft = useCallback(build => run('create', () => api.createCharacterDraft(id, build), storeSnapshot, data => data.draftId), [id, run]);
    const updateDraft = useCallback((draftId, build) => run('update', () => api.updateCharacterDraft(id, draftId, build), storeSnapshot, () => draftId), [id, run]);
    const renameDraft = useCallback((draftId, name) => run('rename', () => api.renameCharacterDraft(id, draftId, name), storeSnapshot, () => draftId), [id, run]);
    const deleteDraft = useCallback(draftId => run('delete', () => api.deleteCharacterDraft(id, draftId), prev => ({ ...prev,
        drafts: prev.drafts.filter(draft => !sameId(draft.draftId, draftId)),
        selectedDraftId: sameId(prev.selectedDraftId, draftId) ? null : prev.selectedDraftId,
    }), () => draftId), [id, run]);

    return { ...state, refresh, selectDraft, fetchDraft, createDraft, updateDraft, renameDraft, deleteDraft };
}
